import { isLeft } from 'fp-ts/lib/Either';
import { fromEither, type TaskEither } from 'fp-ts/lib/TaskEither';
import { use, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { enhancedErrorHandler } from '../../lib/enhancedErrorHandler';
import { getFormValues } from '../../lib/getFormValues';
import { TypedError } from '../../lib/typedError';

import { FormContext, FormProvider } from './FormContext';

interface TRPCErrorData {
  zodError?: {
    fieldErrors?: Record<string, string[]>;
    formErrors?: string[];
  } | null;
}

interface TRPCErrorShape {
  message: string;
  code?: number;
  data?: TRPCErrorData;
}

interface Props<
  Z extends z.ZodObject<z.ZodRawShape>,
> extends React.FormHTMLAttributes<HTMLFormElement> {
  formDataShape: Z;

  validityKey?: string;
  onComplete?(data: z.output<Z>): void;
  onSubmitData?(
    data: TaskEither<TypedError<'InvalidFormData'>, z.output<Z>>,
  ): TaskEither<unknown, unknown>;
}

function RootContent<Z extends z.ZodObject<z.ZodRawShape>>(props: Props<Z>) {
  const { setValid, setError, setSubmitting } = use(FormContext);
  const {
    className,
    formDataShape,
    validityKey,
    onChange,
    onComplete,
    onSubmit,
    onSubmitData,
    ...rest
  } = props;
  const ref = useRef<HTMLFormElement>(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (validityKey && ref.current) {
      const validity = ref.current.checkValidity();
      setValid(validity);
    }
  }, [ref, validityKey]);

  return (
    <form
      noValidate
      ref={ref}
      className={className}
      onChange={(e) => {
        setValid(e.currentTarget.checkValidity());
        onChange?.(e);
      }}
      onSubmit={async (e) => {
        e.preventDefault();
        if (onSubmitData) {
          if (submittingRef.current) {
            return;
          }

          submittingRef.current = true;
          setSubmitting(true);
          setError('');

          try {
            const data = getFormValues(e.currentTarget, formDataShape);

            if (isLeft(data)) {
              console.log('[Form.Root] Validation failed:', data.left);
              setError(data.left.message);
              toast.error('Please fix the form errors', {
                description: data.left.message,
              });
              return;
            }

            const result = await onSubmitData(fromEither(data))();

            if (isLeft(result)) {
              const error = result.left;
              let errorMessage = '';

              // Handle TRPC validation errors
              if (error && typeof error === 'object') {
                if (
                  'data' in error &&
                  error.data &&
                  typeof error.data === 'object'
                ) {
                  const data = error.data as TRPCErrorData;
                  if (data.zodError) {
                    const zodError = data.zodError;
                    if (zodError.fieldErrors) {
                      const fieldErrors = Object.entries(
                        zodError.fieldErrors,
                      ).map(([field, errors]) => {
                        if (Array.isArray(errors) && errors.length > 0) {
                          return errors[0]; // Use first error message
                        }
                        return `${field} is invalid`;
                      });
                      errorMessage = fieldErrors.join('. ');
                    } else if (
                      zodError.formErrors &&
                      Array.isArray(zodError.formErrors)
                    ) {
                      errorMessage = zodError.formErrors.join('. ');
                    }
                  }
                } else if (
                  'shape' in error &&
                  error.shape &&
                  typeof error.shape === 'object'
                ) {
                  const shape = error.shape as TRPCErrorShape;
                  if (shape.message) {
                    // Parse the validation array from the message
                    try {
                      const validationErrors = JSON.parse(shape.message);
                      if (
                        Array.isArray(validationErrors) &&
                        validationErrors.length > 0
                      ) {
                        const messages = validationErrors.map(
                          (err) => err.message || 'Invalid input',
                        );
                        errorMessage = messages.join('. ');
                      } else {
                        errorMessage = String(shape.message);
                      }
                    } catch {
                      errorMessage = String(shape.message);
                    }
                  }
                } else if ('message' in error) {
                  errorMessage = String(error.message);
                }
              }

              // Fallback to enhanced error handler if no message extracted
              if (!errorMessage) {
                errorMessage = enhancedErrorHandler(error).left.message;
              }

              setError(errorMessage);
              toast.error('Failed to update', {
                description: errorMessage,
              });
            } else if (onComplete) {
              onComplete(data.right);
            }
          } finally {
            submittingRef.current = false;
            setSubmitting(false);
          }
        }
        onSubmit?.(e);
      }}
      {...rest}
    />
  );
}

export function Root<Z extends z.ZodObject<z.ZodRawShape>>(props: Props<Z>) {
  return (
    <FormProvider>
      <RootContent {...props} />
    </FormProvider>
  );
}
